import classNames from "classnames";
import * as React from "react";
import EyeIcon from "@assets/images/eye.svg";
import EyeSlashIcon from "@assets/images/eye-closed.svg";

type InputV2Props = React.InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  errorMsg?: string | null;
  leftIcon?: React.ReactNode;
  shouldShowIconBeVisible?: boolean;
};

const preventScrollingOnInput = (e: React.WheelEvent<HTMLInputElement>) => {
  e.currentTarget.blur();
};

export const InputV2 = ({
  label,
  errorMsg,
  className,
  type = "text",
  maxLength,
  onChange,
  shouldShowIconBeVisible,
  leftIcon,
  ...restProps
}: InputV2Props): JSX.Element => {
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (maxLength && e.target.value.length > maxLength) return;
    onChange?.(e);
  };

  return (
    <div className={classNames("flex flex-col", className)}>
      {label && (
        <div className="text-[16px] normal-case font-semibold mb-4">
          {label}
        </div>
      )}
      <div
        className={classNames(
          "w-full flex items-center border border-[#E4E4E4] rounded-lg p-2 focus-within:border-blue-500",
          {
            "border-red-500": !!errorMsg,
            "bg-grey-light": restProps.disabled,
          }
        )}
      >
        {leftIcon && (
          <div className="mr-4 flex items-center justify-center pl-2">
            {leftIcon}
          </div>
        )}

        <input
          className="w-full p-4 focus:outline-none bg-transparent border-none"
          style={{
            border: "none",
            boxShadow: "none",
          }}
          type={type === "password" && isPasswordVisible ? "text" : type}
          onChange={handleChange}
          onWheelCapture={preventScrollingOnInput}
          maxLength={maxLength}
          {...restProps}
        />
        {type === "password" && shouldShowIconBeVisible && (
          <div
            className="ml-2 cursor-pointer flex items-center justify-center pr-2"
            onClick={() => setIsPasswordVisible(!isPasswordVisible)}
          >
            {isPasswordVisible ? <EyeSlashIcon /> : <EyeIcon />}
          </div>
        )}
      </div>
      {errorMsg && <p className="text-red-500 text-sm mt-1">{errorMsg}</p>}
    </div>
  );
};

export default React.memo(InputV2);
